<template>
    <div>
        <v-alert v-if="error" color="error" icon="mdi-alert-octagon-outline" outlined text>
            {{error}}
        </v-alert>

        <v-data-table
            :headers="headers"
            :items="apiKeys"
            :loading="loading"
            sort-by="created_at"
            sort-desc
            class="api-keys"
            dense
            hide-default-footer
            disable-pagination
            no-data-text="No API keys found"
        >
            <template #item.key="{ item }">
                <span class="key">{{item.key}}</span>
            </template>
            <template #item.permission="{ item }">
                <v-chip v-if="item.permission === 'read'" label small outlined color="info">Read</v-chip>
                <v-chip v-else-if="item.permission === 'full_access'" label small outlined color="warning">Full Access</v-chip>
                <v-chip v-else-if="item.permission === 'admin'" label small outlined color="error">Admin</v-chip>
            </template>
            <template #item.created_at="{ item }">
                {{formatDate(item.created_at)}}
            </template>
            <template #item.actions="{ item }">
                <div v-if="deleteKey === item.key" class="py-1">
                    <div class="text-no-wrap">Confirm deleting API key</div>
                    <v-btn x-small color="error" class="ml-1" @click="post({action: 'delete', key_id: item.key_id})">Confirm</v-btn>
                    <v-btn x-small class="ml-1" @click="deleteKey = ''">Cancel</v-btn>
                </div>
                <v-tooltip v-else bottom>
                    <template #activator="{ on }">
                        <v-btn icon small v-on="on" @click="deleteKey = item.key">
                            <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </template>
                    <v-card class="py-1 px-3">
                        delete
                    </v-card>
                </v-tooltip>
            </template>
        </v-data-table>

        <div class="mt-2">
            <v-btn class="primary" @click="openCreateDialog">Create API Key</v-btn>
        </div>

        <v-dialog v-model="createDialog" max-width="500">
            <v-card class="pa-5">
                <div class="d-flex align-center font-weight-bold mb-4">
                    Create API Key
                    <v-spacer />
                    <v-btn icon @click="createDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </div>
                <v-text-field
                    v-model="newKeyName"
                    label="Name"
                    placeholder="e.g., dashboard"
                    outlined
                    dense
                    :error-messages="nameError"
                    class="mb-2"
                />
                <v-select
                    v-model="newKeyPermission"
                    :items="permissionOptions"
                    item-text="label"
                    item-value="value"
                    label="Permission"
                    outlined
                    dense
                    class="mb-2"
                />
                <v-btn block color="primary" @click="createKey" :loading="creating">Create</v-btn>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showKeyDialog" max-width="600" persistent>
            <v-card class="pa-5">
                <div class="d-flex align-center font-weight-bold mb-4">
                    API Key Created
                </div>
                <v-alert color="warning" dense outlined class="mb-4">
                    Make sure to copy your API key now. You won't be able to see it again!
                </v-alert>
                <div class="d-flex align-center">
                    <code class="key-display pa-2 flex-grow-1">{{createdKey}}</code>
                    <CopyButton :text="createdKey" class="ml-2" />
                </div>
                <v-btn block color="primary" class="mt-4" @click="closeKeyDialog">Done</v-btn>
            </v-card>
        </v-dialog>

        <p class="mt-4 grey--text font-size-small">
            Use API keys to access your licenses programmatically via the <code>https://api.coroot.com/v1/licenses</code> endpoint with the <code>X-API-KEY</code> header.
        </p>
        <ul class="grey--text font-size-small">
            <li>Add <code>?full=true</code> to get full license keys (requires Full Access or Admin permission).</li>
            <li>Add <code>?all=true</code> to include archived licenses.</li>
        </ul>
    </div>
</template>

<script>
import CopyButton from "@/components/CopyButton.vue";

export default {
    props: {
        organizationId: String,
    },

    components: {CopyButton},

    data() {
        return {
            loading: false,
            error: '',
            apiKeys: [],
            deleteKey: '',
            createDialog: false,
            showKeyDialog: false,
            newKeyName: '',
            newKeyPermission: 'read',
            createdKey: '',
            creating: false,
            nameError: '',
            permissionOptions: [
                { value: 'read', label: 'Read - can fetch licenses (masked keys)' },
                { value: 'full_access', label: 'Full Access - can fetch licenses (full keys)' },
                { value: 'admin', label: 'Admin - full access + modify (future)' },
            ],
            headers: [
                { text: 'API Key', value: 'key' },
                { text: 'Name', value: 'name', cellClass: 'name-cell' },
                { text: 'Permission', value: 'permission', sortable: false },
                { text: 'Created', value: 'created_at' },
                { text: '', value: 'actions', sortable: false, align: 'end' },
            ],
        };
    },

    mounted() {
        this.get();
    },

    methods: {
        formatDate(timestamp) {
            return new Date(timestamp * 1000).toLocaleDateString();
        },
        openCreateDialog() {
            this.newKeyName = '';
            this.newKeyPermission = 'read';
            this.nameError = '';
            this.createDialog = true;
        },
        createKey() {
            const name = this.newKeyName.trim();
            if (!name) {
                this.nameError = 'Name is required';
                return;
            }
            this.nameError = '';
            this.creating = true;
            this.$api.orgApiKeys(this.organizationId, {action: 'create', name: name, permission: this.newKeyPermission}, (data, meta) => {
                this.creating = false;
                if (meta.error) {
                    this.error = meta.error;
                    return;
                }
                this.createDialog = false;
                this.createdKey = data.key;
                this.showKeyDialog = true;
                this.get();
            });
        },
        closeKeyDialog() {
            this.showKeyDialog = false;
            this.createdKey = '';
        },
        get() {
            this.loading = true;
            this.error = '';
            this.$api.orgApiKeys(this.organizationId, null, (data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.error = meta.error;
                    return;
                }
                this.apiKeys = data || [];
            });
        },
        post(form) {
            this.loading = true;
            this.error = '';
            this.deleteKey = '';
            this.$api.orgApiKeys(this.organizationId, form, (data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.error = meta.error;
                    return;
                }
                this.get();
            });
        },
    },
}
</script>

<style scoped>
.api-keys:deep(tr) {
    background-color: unset !important;
}
.api-keys:deep(th) {
    white-space: nowrap;
}
.api-keys .key {
    font-family: monospace, monospace;
    white-space: nowrap;
}
.api-keys:deep(.name-cell) {
    max-width: 200px;
    word-break: break-word;
}
.key-display {
    font-family: monospace, monospace;
    background-color: var(--background-color-secondary);
    border-radius: 4px;
    word-break: break-all;
}
</style>
